/**
 * FlashGovernor (BL-108a / PT-38 #6) — the photosensitivity governor that wraps
 * the full-screen `ScreenEffects.flash()` seam.
 *
 * The owner reported the red breach/corruption flashes "grow too crazy" in late
 * waves: a W30+ breach storm fires `flash()` once per leaked enemy, so dozens of
 * full-alpha red pulses can stack within a second — a strobe that crosses the
 * Harding / WCAG 2.3.1 three-flash line. This module bounds that storm for
 * EVERYONE (independent of any reduced-motion setting), turning a strobing wall
 * into an urgent-but-safe pulse:
 *
 *   1. STACK CAP — a single granted flash never exceeds {@link STACK_CAP}, so a
 *      `0.4 + damage·0.1 → 0.8` mainframe-hit pulse can't blind on a big leak and
 *      can't compound when several land in the same frame.
 *   2. RATE WINDOW — at most {@link MAX_PER_WINDOW} *perceived* flashes (a bright
 *      rising edge from a dark screen) are granted per rolling {@link WINDOW_MS};
 *      further rising edges are suppressed (the in-flight flash keeps decaying,
 *      and the shake / vignette / HUD still carry the "you're breached" read, so
 *      no information is lost). This is the hard B3 lock: ≤3 red flashes/sec.
 *   3. DECAY-aware perception — only a rising edge ABOVE the current alpha while
 *      the screen is below {@link PERCEPT_FLOOR} reads as a NEW flash; topping up
 *      an already-bright screen is not a fresh strobe and is not rate-limited.
 *      Faint pulses below {@link HAZARD_MIN} ΔL are never a seizure risk and pass
 *      freely (still stack-capped).
 *
 * Determinism-neutral: pure arithmetic over a render clock the caller owns; no
 * DOM, no `gs.rng`, no events, no sim access. The few deliberate canon beats may
 * pass `priority` to bypass the rate window (they still record toward the
 * cadence and still respect the stack cap) — used ONLY for rare, non-storm
 * single beats, so the red-storm B3 guarantee is unaffected.
 */

/** Rolling rate window (ms). */
export const WINDOW_MS = 1000;
/** Max perceived flashes granted per {@link WINDOW_MS} — the B3 seizure line. */
export const MAX_PER_WINDOW = 3;
/** A rising edge only reads as a NEW flash when the current alpha is below this
 *  (a dark→bright transition); above it the screen is already lit. */
export const PERCEPT_FLOOR = 0.12;
/** Below this granted alpha the ΔL is too low to be a photosensitivity flash, so
 *  it is never rate-limited (faint combo/upgrade taps stay fluid). */
export const HAZARD_MIN = 0.18;
/** A single granted flash never exceeds this — caps blinding stacks. */
export const STACK_CAP = 0.6;

export interface FlashRequestOpts {
  /** Rare canon beat — bypass the rate-window suppression (still stack-capped,
   *  still recorded). NEVER set on a repeated combat/breach source. */
  priority?: boolean;
}

export class FlashGovernor {
  /** Render-clock timestamps (ms) of recently granted perceived flashes. */
  private window: number[] = [];

  /**
   * Decide the alpha actually granted for a flash request.
   * @param clockMs  the caller's monotonic render clock, in ms
   * @param requested the requested alpha (already arena-damped by the caller)
   * @param current   the live `flashAlpha` on screen right now
   * @returns the alpha to show — `> current` when a fresh/raised flash is
   *          granted, otherwise `current` (request absorbed; leave the screen).
   */
  request(
    clockMs: number,
    requested: number,
    current: number,
    opts: FlashRequestOpts = {},
  ): number {
    const target = requested < STACK_CAP ? requested : STACK_CAP;
    // Not a rising edge above what's already shown → nothing new to perceive.
    if (target <= current) return current;

    // A genuine NEW flash only when the screen is dark enough AND the pulse is
    // bright enough to register as a luminance step; otherwise it's a harmless
    // top-up / faint tap and passes freely (still capped by `target`).
    const isPerceivedFlash = current < PERCEPT_FLOOR && target >= HAZARD_MIN;
    if (!isPerceivedFlash) return target;

    this.prune(clockMs);
    if (!opts.priority && this.window.length >= MAX_PER_WINDOW) {
      // Rate-limited: suppress the rising edge. The decaying flash + the vignette
      // / shake / HUD still convey the hit — strobe avoided, information kept.
      return current;
    }
    this.window.push(clockMs);
    return target;
  }

  /** Drop timestamps older than the rolling window. */
  private prune(clockMs: number): void {
    const cutoff = clockMs - WINDOW_MS;
    let i = 0;
    while (i < this.window.length && this.window[i]! <= cutoff) i++;
    if (i > 0) this.window.splice(0, i);
  }

  /** Reset the cadence (e.g. on a fresh run). */
  reset(): void {
    this.window.length = 0;
  }
}
